import { useQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { fetchOrganizationCustomerPlansQueryOptions } from "@/api/customerPlans";
import { fetchOrganizationEstablishmentsQueryOptions } from "@/api/establishments";
import { fetchOrganizationFeaturesQueryOptions } from "@/api/organizations";
import { fetchOrganizationSchedulesQueryOptions } from "@/api/schedules";
import { fetchOrganizationStaffQueryOptions } from "@/api/staff";
import { useApiParameters } from "@/hooks/useApiParameters";
import { EnrollmentStepperForm } from "./-components/EnrollmentStepperForm";
import {
	createInitialState,
	EnrollmentFormProvider,
} from "./-context/EnrollmentFormContext";

// Search params schema for pre-selecting customer
const enrollmentNewSearchSchema = z.object({
	customerId: z.coerce.string().optional(),
	customerFirstName: z.coerce.string().optional(),
	customerLastName: z.coerce.string().optional(),
	customerEmail: z.coerce.string().optional(),
	customerIdType: z.coerce.string().optional(),
	customerIdNumber: z.coerce.string().optional(),
});

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/enrollments/new",
)({
	beforeLoad: ({ context }) => {
		if (!context.userId) {
			throw new Error("Not authenticated");
		}
	},
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;
		// Queries - await all data fetching before returning
		await Promise.all([
			context.queryClient.ensureQueryData(
				fetchOrganizationFeaturesQueryOptions(userId, token, orgId),
			),
			context.queryClient.ensureQueryData(
				fetchOrganizationCustomerPlansQueryOptions(userId, token, orgId),
			),
			context.queryClient.ensureQueryData(
				fetchOrganizationSchedulesQueryOptions(userId, token, orgId),
			),
			context.queryClient.ensureQueryData(
				fetchOrganizationEstablishmentsQueryOptions(userId, token, orgId),
			),
			context.queryClient.ensureQueryData(
				fetchOrganizationStaffQueryOptions(token, orgId),
			),
		]);
		return { userId, token, orgId: params.orgId };
	},
	validateSearch: enrollmentNewSearchSchema,
	component: CreateEnrollmentPage,
});

function CreateEnrollmentPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { orgId } = useParams({
		from: "/_auth/app/_app/org/$orgId/enrollments/new",
	});
	const { userId, token } = Route.useLoaderData();
	const search = Route.useSearch();

	// Load data for the provider (data is preloaded by the loader)
	const { data: customerPlansData, isLoading: isLoadingPlans } = useQuery(
		fetchOrganizationCustomerPlansQueryOptions(userId, token, orgId),
	);
	const { data: schedulesData, isLoading: isLoadingSchedules } = useQuery(
		fetchOrganizationSchedulesQueryOptions(userId, token, orgId),
	);
	const { data: establishmentsData, isLoading: isLoadingEstablishments } =
		useQuery(fetchOrganizationEstablishmentsQueryOptions(userId, token, orgId));
	const { data: staffData, isLoading: isLoadingStaff } = useQuery(
		fetchOrganizationStaffQueryOptions(token, orgId),
	);

	// Show loading spinner if any data is still loading
	const isLoading =
		isLoadingPlans ||
		isLoadingSchedules ||
		isLoadingEstablishments ||
		isLoadingStaff;

	if (isLoading) {
		return (
			<div className="h-full flex items-center justify-center">
				<span className="loading loading-spinner loading-lg" />
			</div>
		);
	}

	const handleCancel = () => {
		navigate({
			to: "/app/org/$orgId/enrollments",
			params: { orgId },
			search: { page: 1, limit: 10 },
		});
	};

	const handleSuccess = (enrollmentId: string) => {
		navigate({
			to: "/app/org/$orgId/billing/new",
			params: { orgId },
			search: { enrollmentId },
		});
	};

	// Create initial state for the provider
	const initialState = createInitialState("creatingNewEnrollment");

	// Pre-select customer if provided in URL params
	if (
		search.customerId &&
		search.customerFirstName &&
		search.customerLastName
	) {
		initialState.formData.customerId = search.customerId;
		initialState.selectedCustomer = {
			id: search.customerId,
			firstName: search.customerFirstName,
			lastName: search.customerLastName,
			email: search.customerEmail,
			idType: search.customerIdType,
			idNumber: search.customerIdNumber,
		};
	}

	return (
		<div className="h-full flex flex-col overflow-hidden">
			{/* Compact Header */}
			<div className="flex items-center gap-3 px-4 py-2 border-b border-base-300 bg-base-100 shrink-0">
				<button
					type="button"
					onClick={handleCancel}
					className="btn btn-ghost btn-xs sm:btn-sm shrink-0"
				>
					<ArrowLeft className="w-4 h-4" />
				</button>
				<h1 className="text-sm sm:text-base font-semibold text-base-content truncate">
					{t("enrollments.create.title")}
				</h1>
			</div>

			{/* Stepper Form - takes remaining space */}
			<div className="flex-1 min-h-0 overflow-hidden">
				<EnrollmentFormProvider
					initial={initialState}
					schedulesData={schedulesData || []}
					establishmentsData={establishmentsData || []}
					staffData={staffData || []}
					customerPlansData={customerPlansData || []}
					organizationId={orgId}
					userId={userId}
					token={token}
				>
					<EnrollmentStepperForm onSuccess={handleSuccess} />
				</EnrollmentFormProvider>
			</div>
		</div>
	);
}
